import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';
import { Divider } from '@understory-io/leaf/divider';
import { maxWidthContent, maxWidthContentCheckout } from './shared';
import { Text } from '@understory-io/leaf/text';
import { TextLink, TextLinkButton } from '../link';
import { useLocaleHref } from '~/lib/use-locale-href';

type FooterProps = {
  companyName?: string;
  companyEmail?: string;
  companyPhone?: string;
  showPoweredByUnderstory?: boolean;
  /**
   * Whether the "Cookies" settings link is shown. Must be a server-deterministic
   * value (see `isCookieConsentEnabled`) — do NOT derive it from `window`, or the
   * server and client will render a different number of footer links and break
   * hydration.
   */
  showCookieSettings?: boolean;
  onCookieSettingsClick?: () => void;
  layout: 'default' | 'narrow';
};

export function Footer({
  companyName,
  companyEmail,
  companyPhone,
  showPoweredByUnderstory,
  showCookieSettings,
  onCookieSettingsClick,
  layout,
}: FooterProps) {
  const localeHref = useLocaleHref();

  const year = new Date().getFullYear();
  const copyright = companyName ? `© ${companyName}, ${year}` : null;

  // Build the grid template dynamically so a missing field doesn't reserve an
  // empty row. Each area is included only when the corresponding item renders
  // — see the `gridTemplateAreas` CSS variables on the footer below.
  const present = {
    email: Boolean(companyEmail),
    name: Boolean(companyName),
    phone: Boolean(companyPhone),
    copyright: Boolean(copyright),
    powered: Boolean(showPoweredByUnderstory),
  };
  const hasTabletRow1 = present.name || present.email || present.phone;
  const showDivider = hasTabletRow1; // links is always shown, so row 2 always has content

  const mobileAreas = [
    '"links"',
    present.email && '"email"',
    present.name && '"name"',
    present.phone && '"phone"',
    present.copyright && '"copyright"',
    present.powered && '"powered"',
  ]
    .filter(Boolean)
    .join(' ');

  const tabletAreas = [
    (present.name || present.email) &&
      `"${present.name ? 'name name' : '. .'} ${present.email ? 'email' : '.'}"`,
    present.phone && '". . phone"',
    showDivider && '"divider divider divider"',
    `"${present.copyright ? 'copyright' : '.'} links ."`,
    present.powered && '"powered powered powered"',
  ]
    .filter(Boolean)
    .join(' ');

  const gridVars = {
    '--footer-mobile-areas': mobileAreas,
    '--footer-tablet-areas': tabletAreas,
  } as CSSProperties;

  return (
    <footer
      style={gridVars}
      className={cx(
        footerGridStyles,
        layout === 'narrow' ? maxWidthContentCheckout : maxWidthContent
      )}
    >
      <nav className={linksAreaStyles}>
        <TextLink
          to={localeHref('/:locale?/terms/:type?', { type: undefined })}
          prefetch="intent"
          style="body-small-regular"
          underline={false}
        >
          <FormattedMessage
            defaultMessage="Terms"
            description="Footer link to terms and conditions"
          />
        </TextLink>
        <Separator className={separatorStyles} />
        <TextLink
          to={localeHref('/:locale?/terms/:type?', { type: 'privacy' })}
          prefetch="intent"
          style="body-small-regular"
          underline={false}
        >
          <FormattedMessage
            defaultMessage="Privacy policy"
            description="Footer link to privacy policy"
          />
        </TextLink>
        {showCookieSettings && (
          <>
            <Separator className={separatorStyles} />
            <TextLinkButton
              onClick={onCookieSettingsClick}
              style="body-small-regular"
              underline={false}
            >
              <FormattedMessage
                defaultMessage="Cookies"
                description="Footer button to open cookie preferences"
              />
            </TextLinkButton>
          </>
        )}
      </nav>

      {companyEmail && (
        <div className={emailAreaStyles}>
          <TextLink
            to={`mailto:${companyEmail}`}
            style="body-small-regular"
            underline={false}
          >
            {companyEmail}
          </TextLink>
        </div>
      )}

      {companyName && (
        <Text
          className={nameAreaStyles}
          style="body-small-regular"
          color="primary"
        >
          {companyName}
        </Text>
      )}

      {companyPhone && (
        <Text
          className={phoneAreaStyles}
          style="body-small-regular"
          color="secondary"
        >
          {companyPhone}
        </Text>
      )}

      {showDivider && <Divider className={dividerAreaStyles} />}

      {copyright && (
        <Text
          className={copyrightAreaStyles}
          style="body-small-regular"
          color="secondary"
        >
          {copyright}
        </Text>
      )}

      {showPoweredByUnderstory && (
        <div className={poweredAreaStyles}>
          <PoweredByUnderstory />
        </div>
      )}
    </footer>
  );
}

function PoweredByUnderstory() {
  return (
    <TextLink
      to="https://understory.io"
      target="_blank"
      rel="noopener noreferrer"
      color="tertiary"
      style="caption"
      underline={false}
    >
      <FormattedMessage
        defaultMessage="Powered by Understory"
        description="Powered by Understory branding in the footer"
      />
    </TextLink>
  );
}

function Separator({ className }: { className?: string }) {
  return (
    <Text
      style="body-small-regular"
      color="secondary"
      aria-hidden="true"
      className={className}
    >
      ·
    </Text>
  );
}

// Single CSS grid for the whole footer. Each visible part has its own
// `grid-area` name, and the grid template is built per render from the
// `--footer-*-areas` CSS variables on the footer element. Missing items are
// dropped from the template entirely so they don't reserve empty rows.
//
// On mobile, `row-gap: xsmall` (8px) is the baseline; specific items add a
// `marginTop` to widen the gap above them (xsmall → 16px / medium, large →
// 32px / xlarge). On tablet the baseline becomes `row-gap: small` (12px),
// with `marginTop: small` widening selected gaps to 24px / large.
const footerGridStyles = css({
  display: 'grid',
  columnGap: 'large',
  rowGap: { base: 'xsmall', tablet: 'small' },
  paddingTop: { base: 'large', desktop: '2xlarge' },
  paddingBottom: { base: '[128px]', tablet: 'large', desktop: '2xlarge' },
  paddingInline: { base: 'medium', tablet: 'large' },
  gridTemplateColumns: { base: '1fr', tablet: 'auto auto 1fr' },
  gridTemplateAreas: {
    base: '[var(--footer-mobile-areas)]',
    tablet: '[var(--footer-tablet-areas)]',
  },
});

const linksAreaStyles = css({
  gridArea: 'links',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  // Mobile: Terms · Privacy · Cookies — `tiny` (4px) on each side of dot.
  // Tablet: Terms / Privacy / Cookies — dots hidden, `large` (24px) gap.
  gap: { base: 'tiny', tablet: 'large' },
  marginTop: { tablet: 'small' },
});

const separatorStyles = css({
  display: { base: 'inline', tablet: 'none' },
});

const emailAreaStyles = css({
  gridArea: 'email',
  justifySelf: { tablet: 'end' },
});

const nameAreaStyles = css({
  gridArea: 'name',
  marginTop: { base: 'xsmall' },
});

const phoneAreaStyles = css({
  gridArea: 'phone',
  justifySelf: { tablet: 'end' },
});

const dividerAreaStyles = css({
  gridArea: 'divider',
  display: { base: 'none', tablet: 'block' },
  marginTop: { tablet: 'small' },
});

const copyrightAreaStyles = css({
  gridArea: 'copyright',
  marginTop: { base: 'xsmall', tablet: 'small' },
  // On tablet, the row reads: copyright [32px] Terms [24px] Privacy [24px]
  // Cookies. The grid `column-gap` gives copyright→links a 24px gap; this
  // 8px end margin bumps it to 32px without affecting the link gaps inside.
  marginInlineEnd: { tablet: 'xsmall' },
});

const poweredAreaStyles = css({
  gridArea: 'powered',
  display: 'flex',
  justifyContent: 'center',
  marginTop: { base: 'large', tablet: 'small' },
});
